import { Button, Form, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import moment from "moment/moment";
import { addAutoHyphen } from "../../../common/util";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useDispatch } from "react-redux";
import { changeLoginState } from "../../../../reducer/loginSlice";
import { showAlert } from "../../../../reducer/alertSlice";
import { hideConfirm, showConfirm } from "../../../../reducer/confirmSlice";
import { useNavigate } from "react-router-dom";
import Loading from "../../organisms/Loading";
import { useCertification, useRequestCertification } from "../../../common/hook";

const HelpBox = styled.div`
    display: flex;
    gap: 0 8px;
    align-items: center;
    justify-content: center;
    margin-top: 40px;
    p {
        text-align: center;
        margin: 0;
    }
`;

const Timer = styled.div`
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    z-index: 5;
    font-size: 14px;
`;

export default function Login() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        register,
        watch,
        getValues,
        setValue,
        formState: { isDirty, isValid },
    } = useForm({
        mode: "onChange",
    });

    const watchPhone = watch("phone");
    const timer = useRef();

    const timeLimitSec = 180;
    const timeLimit = moment.utc(timeLimitSec * 1000).format("mm:ss") ;
    const [btnAuthReq, setBtnAuthReq] = useState(false);
    const [sendReq, setSendReq] = useState(false);
    const [authReq, setAuthReq] = useState(false);
    const [startTimer, setStartTimer] = useState(false);
    const [showTimer, setShowTimer] = useState(timeLimit);
    const [autoHyphenValue, setAutoHyphenValue] = useState("");
    const [loading, setLoading] = useState(false);
    
    const [certificationNo, setCertificationNo] = useState("");
    const [certified, setCertified] = useState(); // 인증 완료
    
    
    const requestCertification = useRequestCertification(autoHyphenValue, 1);
    const certification = useCertification(autoHyphenValue, certificationNo, () => {handleClickReqCerti()});


    const handleClickReqCerti = async () => {
        if (autoHyphenValue) {
            setLoading(true);
            if (timer.current) {
                clearInterval(timer.current);
            }

            const result = await requestCertification.refetch();
            if (result.data) {
                if (result.data.status == 200) {
                    setAuthReq(true);
                    dispatch(showAlert({
                        msg: "인증번호가 발송되었습니다."
                    }));
        
                    let sec = 0;
                    setSendReq(true);
                    setStartTimer(true);
                    setShowTimer(moment(timeLimit, "mm:ss").format("mm:ss"));
                    timer.current = setInterval(() => {
                        sec--;
                        setShowTimer(moment(timeLimit, "mm:ss").add(sec, "s").format("mm:ss"));
                        if (sec == timeLimitSec * -1) {
                            clearInterval(timer.current);
                            setShowTimer(timeLimit);
                            setStartTimer(false);
                            dispatch(showConfirm({
                                msg: <>인증번호 입력 시간이 초과되었습니다.<br/>재요청 후 다시 입력해주세요.</>,
                                btnText: "재요청", 
                                func: () => {
                                    dispatch(hideConfirm());
                                    handleClickReqCerti();
                                }
                            }))
                        }
                    },1000);
                }
            }
            setLoading(false);
        }
    };

    const handleClickCertification = async () => {
        if (certificationNo != "") {
            setLoading(true);
            login.refetch();
        } else {
            dispatch(showAlert({
                msg: "인증번호를 입력해주세요."
            }))
        }

    }

    const login = useQuery({
        queryKey: ['login'],
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            return await axios("/login", {
                method: "POST",
                withCredentials: true,
                data: {
                    phone: autoHyphenValue,
                    certificationNo: certificationNo
                }
            }).then((res) => {
                if (res.status == 200) {
                    const accessToken = res.data.data.accessToken;
                    // axios 설정
                    axios.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
                    axios.defaults.withCredentials = true;
                    // 로그인 정보
                    dispatch(changeLoginState({
                        token: accessToken,
                        header: res.data.data.header,
                        menuList: res.data.data.menuList,
                        admin: res.data.data.admin,
                        isSuperAdmin: res.data.data.isSuperAdmin
                    }));
                    sessionStorage.setItem("isAuthenticated", "1");
                }
            
                setSendReq(false);
                setCertified(autoHyphenValue);
                clearInterval(timer.current);
                setCertificationNo("");
                setLoading(false);
                return res;
            }).catch((e) => {
                let msg = <></>;
                let isShowAlert = true;
                switch (e.response.data.code) {
                    case -12:
                        msg = <>등록되지 않은 계정입니다. <br/>번호를 다시 한 번 확인해주세요.</>;
                        break;
                    case -11:
                        msg = <>인증번호가 일치하지 않습니다. <br/>다시 한 번 확인해주세요.</>;
                        break;
                    case -9:
                        isShowAlert = false;
                        navigate("/error/ipAccessRestriction", {
                            state: {
                                ip: e.response.data.data
                            }
                        });
                        break;
                
                    default:
                        msg = e.response.data.msg.ko;
                        break;
                }

                if (isShowAlert) {
                    dispatch(showAlert({
                        msg: msg
                    }))
                }
                
                setSendReq(false);
                setCertified(autoHyphenValue);
                clearInterval(timer.current);
                setCertificationNo("");
                setLoading(false);
            });
        },
        enabled: false
    });


    useEffect(() => {
        if (watchPhone) {
            if (authReq) {
                clearInterval(timer.current);
                setAuthReq(false);
                setSendReq(false);
                setStartTimer(false);
            }

            // 10자리 이상 입력했을 경우 인증번호 요청 활성화
            if (watchPhone.length > 11) {
                setBtnAuthReq(true);
            } else {
                setBtnAuthReq(false);
            }
        }
    },[watchPhone])
    
    return (
        <>
            {loading && <Loading layer={true} />}
            <h1 className="title">로그인</h1>
            <Form.Label className="mt-5">휴대폰 번호 (ID)</Form.Label>
            <InputGroup className="mb-3">
                <InputGroup.Text>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                        <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
                    </svg>
                </InputGroup.Text>
                <Form.Control
                    placeholder="-없이 숫자만 입력하세요"
                    {...register("phone", {
                        required: true,
                    })}
                    value={autoHyphenValue}
                    onInput={(e) => {
                        setAutoHyphenValue(addAutoHyphen(e.currentTarget.value))
                    }}
                    maxLength={13}
                />
                <Button variant="primary" 
                        disabled={!btnAuthReq || startTimer} 
                        onClick={() => {
                            handleClickReqCerti();
                        }}
                >
                    {
                    sendReq
                    ?
                    "재요청"
                    :
                    "인증 요청"
                    }
                </Button>
            </InputGroup>

            <Form.Label>인증번호</Form.Label>
            <InputGroup className="mb-5">
                <InputGroup.Text>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                        <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05" />
                    </svg>
                </InputGroup.Text>
                <Form.Control
                    placeholder="인증번호를 입력하세요"
                    onInput={(e) => {
                        setCertificationNo(e.target.value.replace(/[^0-9]/g, ""));
                    }}
                    value={certificationNo}
                />
                {
                    authReq&&
                    <Timer className="timer">{showTimer}</Timer>
                }
            </InputGroup>

            <Button className="w-100 fw-bold" 
                    variant="primary" 
                    disabled={!isDirty || !isValid}
                    onClick={() => {
                        handleClickCertification();
                    }}
            >
                로그인
            </Button>

            <HelpBox>
                <p>로그인에 문제가 있나요?</p>
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip style={{ fontSize: "12px", position: "fixed" }}>
                            담당자에게 메일을 통해
                            <br />
                            문의해주시기 바랍니다.
                            <br />
                            담당자 이메일: csdigilog@gmail.com
                        </Tooltip>
                    }
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                    </svg>
                </OverlayTrigger>
            </HelpBox>
        </>
    );
}
